import { useState, useEffect, useCallback } from 'react';
import { Plus, Pin, Briefcase, ChevronRight, X, Loader2, RefreshCw } from 'lucide-react';
import { coworkApi } from '../api/coworkApi';
import type { Project, ProjectTemplate } from '../types/cowork';

interface ProjectsPageProps {
  brandId?: string;
  onOpenProject: (id: string) => void;
}

export function ProjectsPage({ brandId, onOpenProject }: ProjectsPageProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showNew, setShowNew] = useState(false);
  // List ALL team projects, regardless of which brand the user is currently
  // viewing — agents may create projects under a different brand_id than the
  // sidebar's active brand. Show brand chip on each card so the source is
  // visible. Eliminates the "I created a project but can't see it" class of bug.
  void brandId;

  const refresh = useCallback(() => {
    setLoading(true);
    coworkApi.projects.list()
      .then((d) => setProjects(d.projects))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh when the user returns to the tab — picks up projects created via
  // chat in another tab / window without forcing a hard reload.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refresh]);

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-[#eaecf0]">
        <div>
          <h1 className="text-[18px] font-semibold text-[#101828]">Projects</h1>
          <p className="text-[12px] text-[#667085] mt-0.5">Campaigns, initiatives, and ongoing programs</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-[#d0d5dd] text-[12px] text-[#475569] hover:bg-[#f9fafb] transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => setShowNew(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#be43ff] text-white text-[13px] hover:bg-[#a835e0] transition-colors"
            style={{ fontWeight: 600 }}
          >
            <Plus className="w-4 h-4" />
            New project
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-auto px-6 py-6">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-[#98a2b3]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : projects.length === 0 ? (
          <EmptyState onCreate={() => setShowNew(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} onOpen={() => onOpenProject(p.id)} onPinToggle={async () => {
                await coworkApi.projects.pin(p.id, !p.pinned).catch((e) => alert(e.message));
                refresh();
              }} />
            ))}
          </div>
        )}
      </div>

      {showNew && (
        <NewProjectModal
          brandId={brandId}
          creating={creating}
          setCreating={setCreating}
          onClose={() => setShowNew(false)}
          onCreated={(p) => {
            setShowNew(false);
            refresh();
            onOpenProject(p.id);
          }}
        />
      )}
    </div>
  );
}

function ProjectCard({ project, onOpen, onPinToggle }: {
  project: Project; onOpen: () => void; onPinToggle: () => void;
}) {
  return (
    <div className="group rounded-xl border border-[#eaecf0] bg-white hover:border-[#be43ff]/30 hover:shadow-sm transition-all p-4 cursor-pointer relative" onClick={onOpen}>
      <button
        onClick={(e) => { e.stopPropagation(); onPinToggle(); }}
        className={`absolute top-3 right-3 p-1.5 rounded-md transition-colors ${
          project.pinned
            ? 'bg-[#fef3c7] text-[#d97706]'
            : 'opacity-0 group-hover:opacity-100 text-[#94a3b8] hover:bg-[#f1f5f9]'
        }`}
        title={project.pinned ? 'Unpin' : 'Pin'}
      >
        <Pin className={`w-3.5 h-3.5 ${project.pinned ? 'fill-current' : ''}`} />
      </button>
      <div className="text-[24px] mb-3">{project.emoji || '📁'}</div>
      <div className="text-[14px] font-semibold text-[#101828] truncate">{project.title}</div>
      <div className="text-[12px] text-[#667085] mt-1 line-clamp-2 min-h-[32px]">
        {project.description || 'No description'}
      </div>
      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#f3f4f6]">
        <span className="text-[11px] text-[#94a3b8]">{new Date(project.createdAt).toLocaleDateString()}</span>
        <ChevronRight className="w-3.5 h-3.5 text-[#94a3b8] group-hover:text-[#be43ff] transition-colors" />
      </div>
    </div>
  );
}

function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-xl bg-[#f8f5ff] flex items-center justify-center mb-4">
        <Briefcase className="w-6 h-6 text-[#be43ff]" />
      </div>
      <h2 className="text-[16px] font-semibold text-[#101828] mb-1">No projects yet</h2>
      <p className="text-[13px] text-[#667085] mb-5 max-w-md">
        A project bundles tasks, OKRs, dashboards, and agents around a single initiative — a launch, a campaign, an always-on program.
      </p>
      <button
        onClick={onCreate}
        className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#be43ff] text-white text-[13px] hover:bg-[#a835e0] transition-colors"
        style={{ fontWeight: 600 }}
      >
        <Plus className="w-4 h-4" />
        Create your first project
      </button>
    </div>
  );
}

function NewProjectModal({
  brandId, creating, setCreating, onClose, onCreated,
}: {
  brandId?: string; creating: boolean; setCreating: (b: boolean) => void;
  onClose: () => void; onCreated: (p: Project) => void;
}) {
  const [templates, setTemplates] = useState<ProjectTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<ProjectTemplate | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    coworkApi.projects.listTemplates().then((d) => setTemplates(d.templates)).catch(() => setTemplates([]));
  }, []);

  async function create(opts: { title: string; description?: string; emoji?: string; template?: string }) {
    setCreating(true);
    try {
      const result = await coworkApi.projects.create({ ...opts, brandId });
      onCreated(result.project);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#eaecf0]">
          <h2 className="text-[15px] font-semibold text-[#101828]">
            {selectedTemplate ? `Configure: ${selectedTemplate.title}` : 'New project'}
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f9fafb]">
            <X className="w-4 h-4 text-[#667085]" />
          </button>
        </div>
        <div className="flex-1 overflow-auto px-6 py-5">
          {!selectedTemplate ? (
            <>
              <p className="text-[12px] text-[#667085] mb-4">Pick a template to pre-fill tasks, OKRs, and agent assignments — or start from scratch.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => { setSelectedTemplate(t); setTitle(t.title); setDescription(t.description); }}
                    className="text-left rounded-lg border border-[#eaecf0] hover:border-[#be43ff]/40 hover:bg-[#f8f5ff]/40 p-3 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="text-[20px]">{t.emoji}</div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-semibold text-[#101828]">{t.title}</div>
                        <div className="text-[11px] text-[#667085] mt-1 line-clamp-2">{t.description}</div>
                        <div className="text-[10px] text-[#94a3b8] mt-2">{t.tasks.length} tasks · {t.okrs.length} OKRs</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              <button
                onClick={() => setSelectedTemplate({
                  id: '__blank__', title: 'New project', emoji: '📁', description: '', tasks: [], okrs: [],
                })}
                className="text-[12px] text-[#be43ff] hover:underline"
                style={{ fontWeight: 500 }}
              >
                Or start from a blank project →
              </button>
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <Field label="Project name">
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g. Q3 Beatly launch"
                  className="w-full px-3 py-2 rounded-lg border border-[#d0d5dd] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40"
                />
              </Field>
              <Field label="Description (optional)">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-[#d0d5dd] text-[13px] focus:outline-none focus:ring-2 focus:ring-[#be43ff]/20 focus:border-[#be43ff]/40 resize-none"
                />
              </Field>
              {selectedTemplate.id !== '__blank__' && (
                <div className="rounded-lg bg-[#f8f5ff] border border-[#be43ff]/15 p-3">
                  <div className="text-[12px] font-semibold text-[#101828] mb-2">This template will create:</div>
                  <ul className="text-[11px] text-[#475569] list-disc list-inside space-y-0.5">
                    <li>{selectedTemplate.tasks.length} pre-filled tasks (assigned to your team's agents by role)</li>
                    <li>{selectedTemplate.okrs.length} OKRs scoped to this project</li>
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        {selectedTemplate && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-[#eaecf0] bg-[#fafbfc]">
            <button
              onClick={() => setSelectedTemplate(null)}
              className="text-[12px] text-[#667085] hover:text-[#101828]"
            >
              ← Back to templates
            </button>
            <button
              disabled={!title.trim() || creating}
              onClick={() => create({
                title: title.trim(),
                description: description.trim() || undefined,
                emoji: selectedTemplate.emoji,
                template: selectedTemplate.id !== '__blank__' ? selectedTemplate.id : undefined,
              })}
              className="px-4 py-2 rounded-lg bg-[#be43ff] text-white text-[13px] hover:bg-[#a835e0] transition-colors disabled:opacity-50"
              style={{ fontWeight: 600 }}
            >
              {creating ? 'Creating…' : 'Create project'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wider text-[#667085] mb-1.5">{label}</div>
      {children}
    </div>
  );
}
